<template>
  <div>
    <form
      v-bind:class="{
        'o-form': true,
        'o-form--sidebar-program-search': isOnResultsPage,
      }"
    >
      <fieldset class="o-form__section s-content-block s-content-block--small">
        <h4 v-if="isOnResultsPage">English as an Additional Language Transfer Search</h4>
        <div v-if="!isOnResultsPage">
          <div v-html="searchHeaderContent"></div>
          <br />
        </div>

        <!-- Program Area -->
        <div class="m-field-group m-field-group--contain">
          <label for="">Program Area</label>
          <div v-for="program in programs" :key="program.id">
            <input
              type="radio"
              :id="`program_radio_${program.id}`"
              name="selectedProgram"
              :value="program.id"
              v-model="filter.selectedProgram"
              :checked="program.id == filter.selectedProgram"
              @change="programSelected"
            />
            <label :for="`program_radio_${program.id}`">
              {{ program.name }} ({{ program.label }})</label
            ><br />
          </div>
        </div>

        <div class="grid-x grid-padding-y">
          <div
            v-bind:class="{
              cell: true,
              'small-12': true,
              'medium-6': !isOnResultsPage,
            }"
          >
            <!-- Level -->
            <div class="m-field-group m-field-group--contain">
              <label for=""
                >Level
                <div
                  v-if="filter.selectedProgram === 1"
                  class="eal-level-tooltip"
                  data-tooltip="EAP Levels include levels I-IV for English for Academic Purposes. CLB Levels include levels 5-8 for Canadian Language Benchmarks. Both assess English proficiency for the purposes of post-secondary admission, program admission, and/or course placement."
                  style="position: relative !important"
                >
                  CLB?
                </div>
              </label>
              <div v-for="level in filteredLevels" :key="level.id">
                <input
                  type="checkbox"
                  :id="`level_checkbox_${level.id}`"
                  :value="level.id"
                  v-model="level.checked"
                  @click="anyLevelChecked = false"
                  @change="levelSelected"
                />
                <label :for="`level_checkbox_${level.id}`">
                  {{
                    level.clbName !== null ? level.name + ' : CLB ' + level.clbLevel : level.name
                  }}</label
                ><br />
              </div>
              <input
                type="checkbox"
                :id="`level_checkbox_any`"
                v-model="anyLevelChecked"
                @click="anyLevelClicked"
                v-on:input="$event.target.checked = true"
              />
              <label :for="`level_checkbox_any`">any</label><br />
            </div>
          </div>

          <!-- Skill -->
          <div class="cell small-12 medium-6">
            <div class="m-field-group m-field-group--contain">
              <label for="">Skill</label>
              <div v-for="skill in filteredSkills" :key="skill.id">
                <input
                  type="checkbox"
                  :id="`skill_checkbox_${skill.id}`"
                  :value="skill.id"
                  v-model="skill.checked"
                  @click="anySkillChecked = false"
                  @change="skillSelected"
                />
                <label :for="`skill_checkbox_${skill.id}`"> {{ skill.name }}</label
                ><br />
              </div>
              <input
                type="checkbox"
                :id="`skill_checkbox_any`"
                v-model="anySkillChecked"
                @click="anySkillClicked"
                v-on:input="$event.target.checked = true"
              />
              <label :for="`skill_checkbox_any`">any</label><br />
            </div>
          </div>
        </div>

        <!-- Institution -->
        <div class="m-field-group m-field-group--contain">
          <label for="">Offering Institution</label>
          <select
            v-model="filter.institutionId"
            :disabled="institutionsLoading || !institutionsInitiallyLoaded"
          >
            <option
              :value="null"
              v-if="institutionsLoading || !institutionsInitiallyLoaded"
              selected
            >
              {{ institutionsPlaceholder }}
            </option>
            <option
              :value="null"
              selected
              v-if="!institutionsLoading || institutionsInitiallyLoaded"
            >
              All Institutions
            </option>
            <option
              v-for="institution in institutions"
              :key="institution.id"
              :value="institution.id"
            >
              {{ institution.code }} - {{ institution.title }}
            </option>
          </select>
        </div>

        <!-- Year -->
        <div class="m-field-group m-field-group--contain">
          <label for="">Academic Year</label>
          <select v-model="filter.academicYear">
            <option :value="null">All Years (2019 onwards)</option>
            <option v-for="year in years" :key="year.year" :value="year.year">
              {{ year.label }}
            </option>
          </select>
        </div>

        <!-- Course Search -->
        <div class="m-field-group m-field-group--contain">
          <label for="course_search_input">Course Search</label>
          <input
            id="course_search_input"
            type="text"
            v-model="filter.searchText"
            maxlength="10"
            @input="trimInput"
          />
        </div>
      </fieldset>

      <button type="button" class="a-button" @click="submitSearch">Search</button>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue'
import { defineProps, defineEmits, defineExpose } from 'vue'
import axios from 'axios'
import _ from 'lodash'

const ealSearchFilterDataStorageName = 'eal-search-params'

const props = defineProps({
  nonce: { type: String, required: true },
  basePagePath: { type: String, default: false },
  resultsPagePath: { type: String, default: false },
  isOnResultsPage: { type: Boolean, default: false },
  searchHeaderContent: { type: String, required: true },
  apiUrls: { type: Object, required: true },
})

const emit = defineEmits(['onSubmit'])

const filter = reactive({
  programs: null,
  skills: null,
  levels: null,
  institutionId: null,
  academicYear:
    new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1,
  searchText: '',
  selectedProgram: null,
})

const programs = ref([])
const skills = ref([])
const levels = ref([])
const institutions = ref([])
const years = ref(getYearsSelectOptionData())
const anySkillChecked = ref(false)
const anyLevelChecked = ref(false)
const institutionsLoading = ref(false)
const institutionsInitiallyLoaded = ref(true)

const filteredSkills = computed(() => {
  return skills.value.filter((skill) => skill.visible)
})

const filteredLevels = computed(() => {
  return levels.value.filter((level) => level.visible)
})

const trimInput = (event) => {
  filter.searchText = sanitizeInput(event.target.value)
}

const sanitizeInput = (input) => {
  return input.replace(/[\0\x08\x09\x1a\n\r"'\\\%]/g, (char) => {
    switch (char) {
      case '\0':
        return '\\0'
      case '\x08':
        return '\\b'
      case '\x09':
        return '\\t'
      case '\x1a':
        return '\\z'
      case '\n':
        return '\\n'
      case '\r':
        return '\\r'
      case '"':
      case "'":
      case '\\':
      case '%':
        return '\\' + char
    }
  })
}

const programSelected = (event) => {
  const selectedProgram = parseInt(event.target.value)
  skills.value.forEach((skill) => {
    skill.visible = skill.programAreaId === selectedProgram
    skill.checked = false
  })

  levels.value.forEach((level) => {
    level.visible = level.programAreaId === selectedProgram
    level.checked = false
  })

  anyLevelChecked.value = true
  anySkillChecked.value = true

  filter.selectedProgram = selectedProgram
  filter.programs = selectedProgram

  filter.institutionId = null
  getInstitutions(selectedProgram)
}

const levelSelected = () => {
  anyLevelChecked.value = levels.value.filter((l) => l.checked).length === 0
}

const skillSelected = () => {
  anySkillChecked.value = skills.value.filter((l) => l.checked).length === 0
}

const anySkillClicked = (event) => {
  if (event.target.checked) {
    skills.value.forEach((skill) => {
      skill.checked = false
    })
  }
  updatePersistence()
}

const anyLevelClicked = (event) => {
  if (event.target.checked) {
    levels.value.forEach((level) => {
      level.checked = false
    })
  }
  updatePersistence()
}

const getCheckedSkills = () => {
  return anySkillChecked.value
    ? null
    : skills.value.filter((skill) => skill.checked).map((skill) => skill.id)
}

const getCheckedLevels = () => {
  return anyLevelChecked.value
    ? null
    : levels.value.filter((level) => level.checked).map((level) => level.id)
}

const getPrograms = () => {
  axios.get(props.apiUrls.searchBaseURL + 'eal/GetProgramAreas').then((response) => {
    programs.value = response.data.sort((a, b) => a.id - b.id)
  })
}

const getSkills = (selectedProgram) => {
  axios.get(props.apiUrls.searchBaseURL + 'eal/GetSkills').then((response) => {
    skills.value = response.data
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((skill) => {
        skill.checked = false
        skill.visible = skill.programAreaId === selectedProgram

        if (_.get(filter, 'skills.length')) {
          skill.checked = filter.skills.includes(skill.id)
        }

        return skill
      })

    if (!_.get(filter, 'skills.length')) {
      anySkillChecked.value = true
    }
  })
}

const getLevels = (selectedProgram) => {
  axios.get(props.apiUrls.searchBaseURL + 'eal/GetLevels').then((response) => {
    levels.value = response.data
      .sort((a, b) => a.id - b.id)
      .map((level) => {
        level.checked = false
        level.visible = level.programAreaId === selectedProgram

        if (_.get(filter, 'levels.length')) {
          level.checked = filter.levels.includes(level.id)
        }

        return level
      })

    if (!_.get(filter, 'levels.length')) {
      anyLevelChecked.value = true
    }
  })
}

const getInstitutions = (selectedProgram) => {
  institutionsLoading.value = true
  axios
    .get(props.apiUrls.searchBaseURL + 'eal/GetInstitutions', {
      params: { programAreaId: selectedProgram },
    })
    .then((response) => {
      institutionsLoading.value = false
      institutionsInitiallyLoaded.value = true
      institutions.value = response.data.sort((a, b) => a.title.localeCompare(b.title))
    })
}

function getYearsSelectOptionData() {
  const startYear = 2021
  const currentYear =
    new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1
  const years = []
  for (let i = currentYear; i >= startYear; i--) {
    years.push({ year: i, label: `${i}/` + (i + 1).toString().slice(-2) })
  }
  return years
}

const getFilterData = () => {
  const filterData = { ...filter }

  filterData.skills = getCheckedSkills()
  filterData.levels = getCheckedLevels()

  return filterData
}

const restorePersistence = () => {
  const persistedData = JSON.parse(sessionStorage.getItem(ealSearchFilterDataStorageName))

  if (persistedData) {
    Object.assign(filter, persistedData)
    getPrograms()
    getSkills(filter.selectedProgram)
    getLevels(filter.selectedProgram)
    getInstitutions(filter.selectedProgram)
  } else {
    filter.selectedProgram = 1
    getPrograms()
    getSkills(1)
    getLevels(1)
    getInstitutions(1)
  }
}

function updatePersistence() {
  const data = getFilterData()

  sessionStorage.setItem(ealSearchFilterDataStorageName, JSON.stringify(data))
}

function clearPersistence() {
  sessionStorage.removeItem(ealSearchFilterDataStorageName)
}

function newSearch() {
  clearPersistence()
  window.location.href = props.basePagePath
}

const submitSearch = () => {
  updatePersistence()

  if (!props.isOnResultsPage) {
    window.location.href = props.resultsPagePath
    return
  }

  submit()
}

const submit = (filterData) => {
  if (!filterData) {
    filterData = getFilterData()
  }

  filterData.searchText = filterData.searchText.trim()
  emit('onSubmit', filterData)
}

watch(filter, updatePersistence, { deep: true })
watch(programs, updatePersistence, { deep: true })
watch(skills, updatePersistence, { deep: true })
watch(levels, updatePersistence, { deep: true })

onMounted(() => {
  restorePersistence()

  if (props.isOnResultsPage) {
    submit(filter)
  }
})

defineExpose({
  newSearch,
})
</script>

<style>
div.eal-level-tooltip[data-tooltip] {
  display: inline-block;
  z-index: 10;
  background: #0088c4;
  color: #fff;
  padding: 0 9.5px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  line-height: 26px;
  width: auto;
  margin: 3px 0 0 8px;
}
div.eal-level-tooltip[data-tooltip]:after {
  width: 300px;
}

[data-tooltip]:after {
  left: 3px !important;
  right: auto;
  max-width: 260px !important;
}
</style>
